/**
 * pollingRegistry — Tier 5B Pass 2 dev-mode polling tracker.
 *
 * Purpose: detect two components polling the same endpoint at the
 * same time (classic double-fetch bug) and surface it via a
 * one-shot `console.warn`. Zero overhead in production.
 *
 * Public API:
 *   - registerPoll(key) → unregister()
 *       Call on poll start (usually from `usePolling` when a `key`
 *       option is given). Returns an idempotent cleanup function.
 *       Warns once per duplicate (registry + call-site pair).
 *   - isPollActive(key) → boolean
 *   - _resetForTests() — test-only reset of internal state.
 *
 * Design notes:
 *   - Counts active subscribers per key. A duplicate is any
 *     registration that raises the count above 1.
 *   - Warn only in dev (`process.env.NODE_ENV !== 'production'`).
 *   - Warns at most once per key per session to avoid log spam.
 */

const _counts = new Map();     // key -> active subscriber count
const _warned = new Set();     // keys that have already warned

const IS_DEV =
  typeof process !== 'undefined' &&
  process.env &&
  process.env.NODE_ENV !== 'production';

export function registerPoll(key) {
  if (!key) return () => {};
  const prev = _counts.get(key) || 0;
  const next = prev + 1;
  _counts.set(key, next);

  if (IS_DEV && next > 1 && !_warned.has(key)) {
    _warned.add(key);
    // eslint-disable-next-line no-console
    console.warn(
      `[usePolling] Duplicate poller detected for "${key}". ` +
      `${next} components are polling this endpoint simultaneously. ` +
      `Consider consolidating into a single shared poller/context.`
    );
  }

  let released = false;
  return function unregister() {
    if (released) return;
    released = true;
    const current = _counts.get(key) || 0;
    if (current <= 1) {
      _counts.delete(key);
    } else {
      _counts.set(key, current - 1);
    }
  };
}

export function isPollActive(key) {
  return (_counts.get(key) || 0) > 0;
}

export function _resetForTests() {
  _counts.clear();
  _warned.clear();
}
